/**
 * A global event dispatcher used to communicate between components.
 */
theme.eventBus = {
  events: {},

  /**
   * Listens for one or more events.
   * @param {Array|String} events - The event or events to listen for.
   * @param {Function} handler - The method to run when invoked.
   */
  listen(events, handler) {
    [...[].concat(events)].forEach((event) => {
      theme.eventBus.events[event] = (theme.eventBus.events[event] || []).concat(handler);
    });
  },

  /**
   * Emits the event, invoking the handlers.
   * @param {String} event - The event to emit.
   * @param {Object} payload - The event payload.
   */
  emit(event, payload) {
    if (!theme.eventBus.events[event]) {
      return;
    }

    [...theme.eventBus.events[event]].forEach((handler) => {
      handler(payload);
    });
  },
};

/**
 * Debounces the run of a function.
 * @param {Function} callback - The function to debounce.
 * @param {Number} wait - The time to wait between running.
 * @returns {Function}
 */
function debounce(callback, wait = 100) {
  let timeout;

  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => callback.apply(this, args), wait);
  };
}

/**
 * Returns the breakpoints defined in `css-variables.liquid`.
 * @returns {Object}
 */
function getBreakpoints() {
  return {
    small: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-small')),
    medium: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-medium')),
    large: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-large')),
    xlarge: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-xlarge')),
  };
}

/**
 * Shifts the slides around to recognise the loop + centered slides settings.
 * @param {Object} swiper - The Swiper instance.
 */
function swiperLoopCenteredSlidesFix(swiper) {
  if (!swiper) {
    return;
  }

  swiper.slidePrev(0);
  swiper.slideNext(0);
}

/**
 * Duplicates slides if a minimum slide count is set, and we're looping.
 * @param {Object} swiper - The Swiper instance.
 */
function swiperLoopMinSlidesFix(swiper) {
  const container = swiper.hostEl;

  if (!swiper.params.loop || !container.hasAttribute('data-min-slides')) {
    return;
  }

  const minSlides = Number(container.dataset.minSlides);
  const slides = [...swiper.slides];
  const slideCount = slides.length;
  const difference = minSlides - slideCount;

  if (slideCount < minSlides) {
    [...Array(difference)].forEach((_, index) => {
      slides[index] && swiper.appendSlide(`<swiper-slide>${slides[index].innerHTML}</swiper-slide>`);
    });
  }
}

/**
 * Replaces elements in a product form with the source product form.
 * @param {HTMLElement} productForm - The product form element.
 * @param {HTMLElement} sourceProductForm - The product form with updated content.
 */
function replaceProductForm(productForm, sourceProductForm) {
  /**
   * Determines if the add-on features are equal when comparing original and source elements.
   *
   * @param {HTMLElement} original - The original element.
   * @param {HTMLElement} source - The incoming element.
   * @returns {Boolean}
   */
  function isOptionSetsEqual(original, source) {
    /**
     * Only replaces the add-on options if they differ.
     */
    return (
      JSON.stringify([...original.children].map(({ dataset }) => dataset.namespace)) ===
      JSON.stringify([...source.children].map(({ dataset }) => dataset.namespace))
    );
  }

  [
    '[js-product-form="price"]',
    '[js-product-form="quantity"]',
    '[js-product-form="pickUp"]',
    '[js-product-form="selector"]',
    '[js-product-form="options"]',
    '[js-product-form="optionDrawers"]',
    '[js-product-form="variantTextOptions"]',
  ].forEach((selector) => {
    replaceElement(productForm, sourceProductForm, selector);
  });

  /**
   * Option sets.
   */
  const optionSetsReplaced = replaceElement(
    productForm,
    sourceProductForm,
    '[js-product-form="optionSets"]',
    isOptionSetsEqual
  );

  const optionSetDrawersReplaced = replaceElement(
    productForm,
    sourceProductForm,
    '[js-product-form="optionSetDrawers"]',
    isOptionSetsEqual
  );

  requestAnimationFrame(() => {
    if (optionSetsReplaced && optionSetDrawersReplaced) {
      productForm.setDefaultOptionSets();
    }

    productForm.setOptionEventListeners();
    productForm.handleOptionValueState();
  });
}

/**
 * A button which toggles the quick add drawer.
 */
customElements.define(
  'quick-add-button',
  class QuickAddToggle extends HTMLElement {
    get button() {
      return this.querySelector('[js-quick-add-button="button"]') ?? this;
    }

    connectedCallback() {
      this.button.addEventListener('click', (event) => {
        event.preventDefault();

        theme.eventBus.emit('quick-add-drawer:open', {
          productHandle: this.getAttribute('product-handle'),
          variantId: Number(this.getAttribute('variant-id')),
        });
      });
    }
  }
);
